import type { FunctionComponentWithChildren } from '@bonsai-components/utility-types';
import {
  borderRadius,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, type SxProps, type Theme } from '@mui/material';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import dynamic from 'next/dynamic';
import { memo, useMemo } from 'react';
import { preprocessMarkdown } from '../../../helpers/markdown-preprocessing.helper';
import { Link } from '../moderne-link/moderne-link.component';
import { MarkdownErrorBoundary } from './markdown.error-boundary';
import { getCodeLanguage } from './markdown-code-language';

const DynamicMermaid = dynamic(
  () => import('../render-mermaid/render-mermaid.component'),
  { ssr: false }
);

// Hoisted so `memo` can bail out: an inline `overrides` literal is a new object
// every render.
export const MARKDOWN_OVERRIDES_INLINE_TEXT: MarkdownToJSX.Overrides = {
  p: { component: 'span' }
};
export const MARKDOWN_OVERRIDES_PLAIN_LINKS: MarkdownToJSX.Overrides = {
  a: { component: 'span' }
};

type ModerneMarkdownProps = {
  allowHtml?: boolean;
  children: string;
  disableCodeFont?: boolean;
  disableCodeFontColor?: boolean;
  overrides?: MarkdownToJSX.Overrides;
  variant?: 'underline' | 'contained';
};

/**
 * Uses `markdown-to-jsx` to process `children`
 * Applies custom color for `code` syntax
 */
const ModerneMarkdownComponent: FunctionComponentWithChildren<
  ModerneMarkdownProps
> = ({
  allowHtml = false,
  children,
  disableCodeFont = false,
  disableCodeFontColor = true,
  overrides,
  variant = 'underline'
}) => {
  // PERF: a sanitizer pass plus several regexes, and this renders per grid cell.
  const markdown = useMemo(() => preprocessMarkdown(children), [children]);

  if (!markdown) {
    return null;
  }

  const codeFontFamily = disableCodeFont
    ? 'inherit'
    : typography.fontFamily.code;

  const styles: SxProps<Theme> = {
    underline: (theme: Theme) => ({
      color: disableCodeFontColor ? 'inherit' : theme.palette.grey[700],
      borderBottom: disableCodeFont
        ? 'none'
        : `1px dotted ${theme.palette.grey[300]}`,
      fontFamily: codeFontFamily,
      fontWeight: disableCodeFont ? 'inherit' : typography.fontWeight.regular,
      fontSize: disableCodeFont ? 'inherit' : typography.fontSize.xs
    }),
    contained: () => ({
      // Semantic code surface (flips: #edebe2 light / #2a2a32 dark). `grey[100]`
      // was a fixed near-white → unreadable white code boxes in dark, since the
      // code text (typography.code.primary) already flips to light.
      backgroundColor: disableCodeFont ? 'transparent' : semanticColors.code.bg,
      borderRadius: disableCodeFont ? 0 : `${borderRadius.xS}px`,
      padding: disableCodeFont
        ? 0
        : `${spacing.spacing_1_4}px ${spacing.spacing_3_4}px`,
      color: disableCodeFont ? 'inherit' : semanticColors.color.text.code,
      fontFamily: codeFontFamily,
      fontWeight: disableCodeFont ? 'inherit' : typography.fontWeight.regular,
      fontSize: disableCodeFont ? 'inherit' : typography.fontSize.xs,
      lineHeight: disableCodeFont ? 'inherit' : 1.75
    })
  };

  const mergedOverrides = {
    a: {
      component: Link,
      props: {
        external: true
      }
    },
    code: {
      component: MarkdownCode,
      props: {
        sx: styles[variant]
      }
    },
    ...overrides
  };

  return (
    <MarkdownErrorBoundary>
      <Markdown
        options={{
          disableParsingRawHTML: !allowHtml,
          overrides: mergedOverrides
        }}
      >
        {markdown}
      </Markdown>
    </MarkdownErrorBoundary>
  );
};

export const ModerneMarkdown = memo(ModerneMarkdownComponent);

type MarkdownCodeProps = {
  sx?: SxProps<Theme>;
  className?: string;
};

const MarkdownCode: FunctionComponentWithChildren<MarkdownCodeProps> = ({
  className,
  children,
  sx
}) => {
  const language = getCodeLanguage(className);

  if (language === 'mermaid') {
    return (
      <Box>
        <DynamicMermaid>{children}</DynamicMermaid>
      </Box>
    );
  }

  return (
    <Box component="code" sx={sx}>
      {children}
    </Box>
  );
};
